import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import Loadable from './components/Loadable';
import ProtectedRoute from './components/ProtectedRoute';
import { Role } from './types/auth.type';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import OverviewDashboard from './pages/dashboard/OverviewDashboard';
import TasksPage from './pages/dashboard/TasksPage';
import ObjectivesPage from './pages/dashboard/ObjectivesPage';
import ObjectiveDetailsPage from './pages/dashboard/ObjectiveDetailsPage';
import MyTasksPage from './pages/dashboard/MyTasksPage';
import TeamsPage from './pages/dashboard/TeamsPage';
import TeamDetailsPage from './pages/dashboard/TeamDetailsPage';
import MembersPage from './pages/dashboard/MembersPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import SeqAIPage from './pages/dashboard/SeqAIPage';

// Marketing
const MarketingLayout = Loadable(lazy(() => import('./pages/marketing/MarketingLayout')));
const HomePage = Loadable(lazy(() => import('./pages/marketing/HomePage')));
const PrivacyPolicyPage = Loadable(lazy(() => import('./pages/marketing/PrivacyPolicyPage')));

// Auth
const Login = Loadable(lazy(() => import('./pages/auth/login')));
const Signup = Loadable(lazy(() => import('./pages/auth/signup')));
const ForgotPassword = Loadable(lazy(() => import('./pages/auth/forgotPassword')));
const ResetPassword = Loadable(lazy(() => import('./pages/auth/resetPassword')));
const GoogleCallback = Loadable(lazy(() => import('./pages/auth/GoogleCallback')));
const GoogleSuccess  = Loadable(lazy(() => import('./pages/auth/GoogleSuccess')));

// Errors
const NotFound = Loadable(lazy(() => import('./pages/error/notFound')));
const NotAuthorized = Loadable(lazy(() => import('./pages/error/NotAuthorized')));

// Dashboard pages are imported directly so switching tabs does not wait on a new script.

export const marketingRoutes = {
  path: '/',
  element: <MarketingLayout navVariant="hero" />,
  children: [
    { path: '', element: <HomePage /> },
    { path: 'privacy', element: <PrivacyPolicyPage /> },
  ],
};

export const authRoutes = {
  path: '/',
  children: [
    { path: 'login', element: <Login /> },
    { path: 'signup', element: <Signup /> },
    { path: 'forgot-password', element: <ForgotPassword /> },
    { path: 'reset-password', element: <ResetPassword /> },
    { path: 'settings/google/callback', element: <GoogleCallback /> },
    { path: 'google/success', element: <GoogleSuccess /> },
  ],
};

export const dashboardRoutes = {
  path: 'dashboard',
  element: (
    <ProtectedRoute requiredRoles={[Role.MANAGER]}>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    { path: '', element: <OverviewDashboard /> },
    { path: 'my-tasks', element: <MyTasksPage /> },
    { path: 'tasks', element: <TasksPage /> },
    { path: 'objectives', element: <ObjectivesPage /> },
    { path: 'objectives/:objectiveId', element: <ObjectiveDetailsPage /> },
    { path: 'teams', element: <TeamsPage /> },
    { path: 'teams/:teamId', element: <TeamDetailsPage /> },
    { path: 'members', element: <MembersPage /> },
    { path: 'access-requests', element: <Navigate to="/dashboard/members" replace /> },
    { path: 'seqai', element: <SeqAIPage /> },
    { path: 'settings', element: <SettingsPage /> },
  ],
};

export const router = createBrowserRouter([
  marketingRoutes,
  authRoutes,
  dashboardRoutes,
  { path: 'not-authorized', element: <NotAuthorized /> },
  { path: '*', element: <NotFound /> },
]);
